<template>
  <div>
    <div class="hero mb-5 d-flex">
      <div class="content container m-auto text-center">
        <h1>About Trident Network</h1>
        <h5>
          Our mission is to provide fast and stable connectivity in unreachable
          areas.
        </h5>
      </div>
    </div>

    <div class="container mt-5">
      <!-- Company Overview -->
      <div class="row justify-content-center">
        <div class="col-lg-8">
          <div class="about-intro text-center mb-5">
            <div class="about-icon">
              <i class="fas fa-broadcast-tower"></i>
            </div>
            <h2>Connecting the Unreachable</h2>
            <p class="lead-text">
              Trident Network is a registered Internet Service Provider (ISP)
              in the Philippines, delivering fiber and wireless broadband
              connectivity to residential and commercial customers in rural
              and underserved communities that mainstream providers have left
              behind. We believe everyone deserves fast, reliable internet —
              no matter where they are.
            </p>
          </div>
        </div>
      </div>

      <!-- Business Model & Services -->
      <div class="row justify-content-center mb-5">
        <div class="col-lg-10">
          <div class="row">
            <div class="col-md-6 mb-4">
              <div class="about-card">
                <div class="about-card-icon">
                  <i class="fas fa-sitemap"></i>
                </div>
                <h5>How We Operate</h5>
                <p>
                  Trident Network operates as a local Internet Service Provider
                  using a combination of fiber-optic and point-to-point/multipoint
                  wireless technology to deliver broadband internet to end users.
                  We deploy and maintain our own relay infrastructure, allowing us
                  to extend coverage to areas where traditional ISPs cannot reach
                  due to geographic or logistical constraints. Revenue is generated
                  through monthly subscription plans offered to residential and
                  small business customers.
                </p>
              </div>
            </div>
            <div class="col-md-6 mb-4">
              <div class="about-card">
                <div class="about-card-icon">
                  <i class="fas fa-concierge-bell"></i>
                </div>
                <h5>Services We Provide</h5>
                <ul class="about-list">
                  <li>
                    <strong>Trident Fiber</strong> — High-speed fiber-optic broadband
                    with plans ranging from 50 Mbps to 500 Mbps
                  </li>
                  <li>
                    <strong>Trident Wireless</strong> — Wireless broadband using
                    point-to-point/multipoint technology for areas where fiber
                    infrastructure is not yet available
                  </li>
                  <li>
                    <strong>Web & Email Hosting</strong> — Affordable shared hosting,
                    WordPress hosting, and business email hosting solutions
                  </li>
                  <li>
                    <strong>Managed Installation</strong> — Free on-site installation
                    and equipment setup with ongoing technical support
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Target Customers & Coverage -->
      <div class="row justify-content-center mb-5">
        <div class="col-lg-10">
          <div class="row">
            <div class="col-md-6 mb-4">
              <div class="about-card">
                <div class="about-card-icon">
                  <i class="fas fa-users"></i>
                </div>
                <h5>Who We Serve</h5>
                <p>
                  Our primary customers are residential households and small
                  businesses located in rural and semi-urban areas in the Bicol
                  Region, Philippines — communities that are often branded as
                  "unreachable" by mainstream Internet Service Providers.
                </p>
                <p>
                  We cater to families who need connectivity for work-from-home
                  and online education, small business owners who rely on the
                  internet for daily operations, and individuals in remote
                  barangays who have limited or no access to stable broadband
                  service.
                </p>
              </div>
            </div>
            <div class="col-md-6 mb-4">
              <div class="about-card">
                <div class="about-card-icon">
                  <i class="fas fa-map-marked-alt"></i>
                </div>
                <h5>Coverage & Infrastructure</h5>
                <p>
                  Trident Network currently operates <strong>15+ relay stations</strong>
                  strategically positioned across the region, providing coverage to
                  multiple municipalities and barangays.
                </p>
                <p>
                  Our infrastructure is continuously expanding as we invest in
                  fiber-optic lines and additional wireless relay points to
                  bring more communities online. We maintain a
                  <strong>99.9% uptime guarantee</strong> backed by 24/7
                  technical support and proactive network monitoring.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Our Promise -->
      <div class="row justify-content-center mb-5">
        <div class="col-lg-10">
          <div class="promises-section">
            <div class="text-center mb-4">
              <h6 class="section-label">OUR PROMISE</h6>
              <h3>What We Guarantee</h3>
            </div>
            <div class="row">
              <div class="col-md-4 mb-3">
                <div class="promise-item">
                  <div class="promise-number">01</div>
                  <h6>Quality Service</h6>
                  <p>
                    Straightforward customer service that puts your connectivity
                    needs first.
                  </p>
                </div>
              </div>
              <div class="col-md-4 mb-3">
                <div class="promise-item">
                  <div class="promise-number">02</div>
                  <h6>Fast Resolution</h6>
                  <p>
                    Your concerns are not just heard and received but resolved as
                    soon as possible.
                  </p>
                </div>
              </div>
              <div class="col-md-4 mb-3">
                <div class="promise-item">
                  <div class="promise-number">03</div>
                  <h6>Focused Attention</h6>
                  <p>
                    Our concentrated, focused service gives us more time to
                    constantly improve.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Information -->
      <div class="row justify-content-center mb-5">
        <div class="col-lg-10">
          <div class="contact-block">
            <div class="text-center mb-4">
              <h6 class="section-label">CONTACT INFORMATION</h6>
              <h3>Get in Touch</h3>
            </div>
            <div class="row justify-content-center">
              <div class="col-md-4 mb-3">
                <div class="contact-item text-center">
                  <i class="fas fa-envelope"></i>
                  <h6>General Support</h6>
                  <a href="mailto:support@tridentnetwork.com.ph">support@tridentnetwork.com.ph</a>
                </div>
              </div>
              <div class="col-md-4 mb-3">
                <div class="contact-item text-center">
                  <i class="fas fa-phone-alt"></i>
                  <h6>Hotline</h6>
                  <a href="tel:09609340459">0960 934 0459</a>
                </div>
              </div>
              <div class="col-md-4 mb-3">
                <div class="contact-item text-center">
                  <i class="fas fa-flag"></i>
                  <h6>Report Abuse</h6>
                  <a href="mailto:abuse@tridentnetwork.com.ph">abuse@tridentnetwork.com.ph</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="cta-section">
      <div class="container text-center">
        <h2>Ready to Get Connected?</h2>
        <p>
          Check if your area is within our coverage and get started with
          fast, reliable internet today.
        </p>
        <a href="/#contact" class="btn btn-secondary btn-lg">
          Contact Us <i class="fas fa-arrow-right ms-2"></i>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  title: "Trident Network - About",
  mounted() {
    window.scrollTo(0, 0);
  }
};
</script>

<style lang="scss" scoped>
.section-label {
  color: #00ffd0;
  font-size: 13px;
  letter-spacing: 3px;
  font-weight: 700;
  margin-bottom: 8px;
}

.about-intro {
  .about-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #00ffd0, #1b86b9);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;

    i {
      font-size: 32px;
      color: #000;
    }
  }

  .lead-text {
    font-size: 18px;
    color: #d0d0d0;
    line-height: 1.7;
  }
}

.about-card {
  background: #131516;
  border: 1px solid #242424;
  border-radius: 12px;
  padding: 30px;
  height: 100%;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: #333;
  }

  .about-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: #1c2225;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    i {
      font-size: 20px;
      color: #00ffd0;
    }
  }

  h5 {
    font-size: 20px;
    margin-bottom: 12px;
  }

  p {
    color: #b0b0b0;
    font-size: 16px;
    line-height: 1.7;
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.about-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    padding: 10px 0;
    border-bottom: 1px solid #242424;
    color: #b0b0b0;
    font-size: 15px;
    line-height: 1.6;

    &:last-child {
      border-bottom: none;
    }

    strong {
      color: #00ffd0;
    }
  }
}

.promises-section {
  background: #131516;
  border: 1px solid #242424;
  border-radius: 12px;
  padding: 40px 30px;
}

.promise-item {
  padding: 20px;
  text-align: center;

  .promise-number {
    font-size: 36px;
    font-weight: 700;
    color: #00ffd0;
    line-height: 1;
    margin-bottom: 12px;
  }

  h6 {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  p {
    color: #b0b0b0;
    font-size: 15px;
    margin-bottom: 0;
  }
}

.contact-block {
  background: #131516;
  border: 1px solid #242424;
  border-radius: 12px;
  padding: 40px 30px;
}

.contact-item {
  padding: 20px;

  i {
    font-size: 28px;
    color: #00ffd0;
    margin-bottom: 12px;
  }

  h6 {
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  a {
    color: #b0b0b0;
    text-decoration: none;
    font-size: 14px;
    word-break: break-all;

    &:hover {
      color: #00ffd0;
    }
  }
}

.cta-section {
  padding: 80px 0;
  border-top: 1px solid #242424;
  margin-top: 40px;

  h2 {
    margin-bottom: 12px;
  }

  p {
    color: #b0b0b0;
    font-size: 18px;
    margin-bottom: 28px;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
